/**
 * Chart arithmetic — the whole of it, in pure functions.
 *
 * Stats draws twelve panels and takes **no chart library**, which was weighed and decided (日本-337,
 * 8 Aug 2026): the design contains one `<svg>` in 85 kB of markup and is otherwise CSS boxes, only
 * four of the twelve panels need a scale at all, and every mark's colour is a `tokens.css` custom
 * property that DOM and SVG inherit for free. Recharts would have been +147 kB gzipped — a 67%
 * increase on the whole app — to draw eleven stacked proportion bars that need no axis.
 *
 * What that decision *costs* is this file, and the reason it is worth paying is here: the
 * interesting bugs in a chart are arithmetic, and arithmetic in a pure module is the shape the rest
 * of this app already tests — `libraryDrag.ts`, `menuPlacement.ts`, `logTabs.ts`, `schedule.ts`.
 * Inside a library the same maths is untestable by that pattern.
 *
 * **Nothing here knows about React, colour or pixels-as-units.** Components take numbers from these
 * functions and put them in `style`.
 */

/** Hold `value` inside `[min, max]`. `NaN` resolves to `min` rather than propagating. */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * `value` as a percentage of `max`, clamped to 0–100.
 *
 * The clamp is not defensive padding. Coverage's JMdict priority rows divide a tag count by a
 * universe **derived from the dictionary** rather than read from a list, so the two are not
 * guaranteed to be a subset relationship — and a bar rendering at 103% would overflow its track and
 * read as a layout bug rather than as the data question it is. A zero `max` gives 0, not `NaN`.
 */
export function percent(value: number, max: number): number {
  if (max <= 0) return 0;
  return clamp((value / max) * 100, 0, 100);
}

/**
 * Round a maximum up to something an axis can be labelled with.
 *
 * A scale topping out at the data's exact peak puts the tallest bar flush against the frame and
 * gives the axis a label like `247`. This lifts it to the next 1/2/5×10ⁿ step.
 */
export function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

/**
 * Evenly spaced tick values across `[min, max]`, inclusive of both ends.
 *
 * `count` is the number of intervals, so `ticks(0, 100, 4)` gives five labels. Deliberately not
 * d3's "nice" tick algorithm: every axis in this design has a domain somebody chose (60–100% for
 * retention, 50–100% for calibration), so evenly dividing the domain you asked for beats being
 * handed a different one.
 */
export function ticks(min: number, max: number, count: number): number[] {
  if (count < 1 || !Number.isFinite(min) || !Number.isFinite(max)) return [min];
  const step = (max - min) / count;
  return Array.from({ length: count + 1 }, (_, i) => min + step * i);
}

/**
 * Which shade a day's review count falls in, against the server's thresholds.
 *
 * `-1` means a **missed day**, which is not the palest bucket — the design gives it a paper fill and
 * a red ring, because he studies nearly every day and so the rare blanks are the information. The
 * server computes the thresholds (quantiles of his own distribution); this only reads them, so the
 * grid and the streak beside it cannot be bucketing against different numbers.
 */
export function bucketOf(count: number, thresholds: readonly number[]): number {
  if (count <= 0) return -1;
  let bucket = 0;
  thresholds.forEach((floor, index) => {
    if (count >= floor) bucket = index;
  });
  return bucket;
}

/**
 * Segment widths for a stacked proportion bar, summing to **exactly** 100.
 *
 * Rounding each segment independently leaves a sub-pixel gap at the end of the track, which on the
 * coverage bars reads as a fifth category nobody defined. Largest-remainder: floor everything, then
 * hand the leftover units to whichever segments lost the most in the rounding.
 *
 * A zero total gives all-zero widths rather than `NaN`, so an empty tier renders as an empty track.
 */
export function segmentWidths(values: readonly number[], precision = 2): number[] {
  const total = values.reduce((sum, v) => sum + Math.max(0, v), 0);
  if (total <= 0) return values.map(() => 0);

  const units = 100 * 10 ** precision;
  const exact = values.map((v) => (Math.max(0, v) / total) * units);
  const floored = exact.map(Math.floor);
  let remainder = units - floored.reduce((sum, v) => sum + v, 0);

  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);

  for (const { index } of order) {
    if (remainder <= 0) break;
    floored[index] = (floored[index] ?? 0) + 1;
    remainder -= 1;
  }
  return floored.map((v) => v / 10 ** precision);
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Map a series into SVG `points` strings, with `y` inverted for screen coordinates.
 *
 * `domain` is the value range the y-axis covers — for the retention trend that is 60–100%, chosen
 * rather than derived, because an auto-fitted axis makes a flat year look like a crisis. A value
 * outside it is clamped rather than drawn off-canvas.
 *
 * **It returns one string per unbroken RUN, and that is the whole reason it is not one string.** A
 * gap in the series — a 30-day window with too few reviews to have a rate — has to break the line,
 * and simply *omitting* the point does not break anything: `<polyline>` draws a straight segment
 * from the point before the gap to the point after it, so a two-month break away from the
 * collection renders as a confident straight line through a period with no data. That is a worse
 * lie than drawing the gap as zero, because it looks like a measurement. Each run gets its own
 * `<polyline>`. A run of a single point is still returned and simply draws nothing — an isolated
 * sample between two gaps has no line to be part of, which is the honest rendering of it.
 */
export function polyline(
  values: readonly (number | null)[],
  { width, height, domain }: { width: number; height: number; domain: [number, number] },
): string[] {
  const [low, high] = domain;
  const span = high - low || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const runs: string[][] = [];
  let run: string[] = [];
  values.forEach((value, index) => {
    if (value === null || !Number.isFinite(value)) {
      if (run.length) runs.push(run);
      run = [];
      return;
    }
    const x = index * step;
    const y = height - (clamp(value, low, high) - low) / span * height;
    run.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  });
  if (run.length) runs.push(run);
  return runs.map((points) => points.join(" "));
}

/** A rate as a display percentage, or `null` when there is nothing to divide. */
export function rate(passed: number, failed: number): number | null {
  const total = passed + failed;
  return total <= 0 ? null : (passed / total) * 100;
}
